/**
 * Fleet Service
 * Fleet (filo) işlemleri için service
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import {
  Fleet,
  PaginatedResponse,
  BaseQueryParams,
  ID,
  ExportFormat
} from '../types/api.types';

/**
 * Fleet query parameters
 */
export interface FleetQueryParams extends BaseQueryParams {
  tenantId?: ID | ID[];
  status?: string | string[];
  managerId?: ID | ID[];
  unitCountFrom?: number;
  unitCountTo?: number;
  includeUnits?: boolean;
  includeStatistics?: boolean;
}

/**
 * Fleet create/update request
 */
export interface FleetRequest {
  fleetId: string;
  name: string;
  description?: string;
  tenantId: ID;
  managerId?: ID;
  status?: string;
  metadata?: Record<string, any>;
}

/**
 * Fleet statistics
 */
export interface FleetStatistics {
  fleetId: ID;
  totalUnits: number;
  operationalUnits: number;
  maintenanceUnits: number;
  outOfServiceUnits: number;
  totalAlarms: number;
  activeAlarms: number;
  averageUptime: number;
  totalDistance: number;
  averageFuelConsumption?: number;
  maintenanceCost: number;
  performanceScore: number;
}

/**
 * Fleet unit assignment
 */
export interface FleetUnitAssignment {
  fleetId: ID;
  unitIds: ID[];
  assignedBy: string;
  assignedAt?: Date;
}

/**
 * Fleet Service Interface
 */
export interface IFleetService {
  // CRUD Operations
  getFleets(params?: FleetQueryParams): Promise<PaginatedResponse<Fleet>>;
  getFleet(id: ID): Promise<Fleet>;
  createFleet(fleet: FleetRequest): Promise<Fleet>;
  updateFleet(id: ID, fleet: Partial<FleetRequest>): Promise<Fleet>;
  deleteFleet(id: ID): Promise<void>;
  
  // Special Queries
  getFleetsByTenant(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Fleet>>;
  getFleetByFleetId(fleetId: string): Promise<Fleet>;
  searchFleets(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Fleet>>;
  
  // Fleet Management
  getFleetStatistics(fleetId: ID): Promise<FleetStatistics>;
  getFleetPerformance(fleetId: ID, dateRange?: { startDate: Date; endDate: Date }): Promise<any>;
  getFleetUnits(fleetId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>>;
  
  // Unit Assignment
  assignUnitsToFleet(assignment: FleetUnitAssignment): Promise<void>;
  removeUnitsFromFleet(fleetId: ID, unitIds: ID[]): Promise<void>;
  transferUnits(sourceFleetId: ID, targetFleetId: ID, unitIds: ID[]): Promise<void>;
  
  // Maintenance & Operations
  getFleetMaintenanceSchedule(fleetId: ID): Promise<any[]>;
  getFleetAlerts(fleetId: ID): Promise<any[]>;
  getFleetLocation(fleetId: ID): Promise<any>;
  
  // Reports & Export
  exportFleetReport(fleetId: ID, format: ExportFormat): Promise<Blob>;
  generateFleetPerformanceReport(fleetId: ID): Promise<any>;
  
  // Configuration
  updateFleetConfig(fleetId: ID, config: any): Promise<any>;
  getFleetConfig(fleetId: ID): Promise<any>;
}

/**
 * Fleet Service Implementation
 */
export class FleetService implements IFleetService {
  async getFleets(params?: FleetQueryParams): Promise<PaginatedResponse<Fleet>> {
    try {
      const endpoint = ENDPOINTS.FLEETS.LIST;
      const cacheKey = this.generateCacheKey('fleets', params);
      
      return await apiClient.getList<Fleet>(endpoint, params, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000 // 2 dakika
      });
    } catch (error) {
      console.error('Get fleets error:', error);
      throw error;
    }
  }

  async getFleet(id: ID): Promise<Fleet> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.FLEETS.DETAIL, id);
      const cacheKey = `fleet_${id}`;
      
      const response = await apiClient.get<Fleet>(endpoint, id, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000 // 5 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet ${id} error:`, error);
      throw error;
    }
  }

  async createFleet(fleet: FleetRequest): Promise<Fleet> {
    try {
      const endpoint = ENDPOINTS.FLEETS.CREATE;
      const response = await apiClient.customPost<Fleet>(endpoint, fleet);
      
      this.clearFleetsCache();
      
      return response.data;
    } catch (error) {
      console.error('Create fleet error:', error);
      throw error;
    }
  }

  async updateFleet(id: ID, fleet: Partial<FleetRequest>): Promise<Fleet> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.FLEETS.UPDATE, id);
      const response = await apiClient.customPut<Fleet>(endpoint, fleet);
      
      this.clearFleetCache(id);
      this.clearFleetsCache();
      
      return response.data;
    } catch (error) {
      console.error(`Update fleet ${id} error:`, error);
      throw error;
    }
  }

  async deleteFleet(id: ID): Promise<void> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.FLEETS.DELETE, id);
      await apiClient.delete(endpoint, id);
      
      this.clearFleetCache(id);
      this.clearFleetsCache();
    } catch (error) {
      console.error(`Delete fleet ${id} error:`, error);
      throw error;
    }
  }

  async getFleetsByTenant(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Fleet>> {
    const tenantParams: FleetQueryParams = {
      ...params,
      tenantId
    };
    
    return this.getFleets(tenantParams);
  }

  async getFleetByFleetId(fleetId: string): Promise<Fleet> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/by-fleet-id/${fleetId}`;
      const cacheKey = `fleet_by_fleet_id_${fleetId}`;
      
      const response = await apiClient.customGet<Fleet>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet by fleetId ${fleetId} error:`, error);
      throw error;
    }
  }

  async searchFleets(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Fleet>> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/search`;
      const searchParams = {
        ...params,
        q: query
      };
      
      const cacheKey = this.generateCacheKey('fleets_search', { query, ...params });
      
      return await apiClient.getList<Fleet>(endpoint, searchParams, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
    } catch (error) {
      console.error('Search fleets error:', error);
      throw error;
    }
  }

  async getFleetStatistics(fleetId: ID): Promise<FleetStatistics> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/statistics`;
      const cacheKey = `fleet_stats_${fleetId}`;
      
      const response = await apiClient.customGet<FleetStatistics>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet statistics ${fleetId} error:`, error);
      throw error;
    }
  }

  async getFleetPerformance(fleetId: ID, dateRange?: { startDate: Date; endDate: Date }): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/performance`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheTTL: 60 * 1000,
        params: dateRange
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet performance ${fleetId} error:`, error);
      throw error;
    }
  }

  async getFleetUnits(fleetId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.FLEETS.UNITS, fleetId);
      const cacheKey = this.generateCacheKey(`fleet_units_${fleetId}`, params);
      
      return await apiClient.getList<any>(endpoint, params, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
    } catch (error) {
      console.error(`Get fleet units ${fleetId} error:`, error);
      throw error;
    }
  }

  async assignUnitsToFleet(assignment: FleetUnitAssignment): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${assignment.fleetId}/assign-units`;
      
      await apiClient.customPost<void>(endpoint, assignment);
      
      this.clearFleetCache(assignment.fleetId);
      this.clearFleetsCache();
    } catch (error) {
      console.error('Assign units to fleet error:', error);
      throw error;
    }
  }

  async removeUnitsFromFleet(fleetId: ID, unitIds: ID[]): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/remove-units`;
      
      await apiClient.customPost<void>(endpoint, { unitIds });
      
      this.clearFleetCache(fleetId);
      this.clearFleetsCache();
    } catch (error) {
      console.error(`Remove units from fleet ${fleetId} error:`, error);
      throw error;
    }
  }

  async transferUnits(sourceFleetId: ID, targetFleetId: ID, unitIds: ID[]): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/transfer-units`;
      
      await apiClient.customPost<void>(endpoint, {
        sourceFleetId,
        targetFleetId,
        unitIds
      });
      
      this.clearFleetCache(sourceFleetId);
      this.clearFleetCache(targetFleetId);
      this.clearFleetsCache();
    } catch (error) {
      console.error('Transfer units error:', error);
      throw error;
    }
  }

  async getFleetMaintenanceSchedule(fleetId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/maintenance-schedule`;
      const cacheKey = `fleet_maintenance_schedule_${fleetId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get fleet maintenance schedule ${fleetId} error:`, error);
      throw error;
    }
  }

  async getFleetAlerts(fleetId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/alerts`;
      const cacheKey = `fleet_alerts_${fleetId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get fleet alerts ${fleetId} error:`, error);
      throw error;
    }
  }

  async getFleetLocation(fleetId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/location`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheTTL: 30 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet location ${fleetId} error:`, error);
      throw error;
    }
  }

  async exportFleetReport(fleetId: ID, format: ExportFormat): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/export-report`;
      return await apiClient.exportData(endpoint, format);
    } catch (error) {
      console.error(`Export fleet report ${fleetId} error:`, error);
      throw error;
    }
  }

  async generateFleetPerformanceReport(fleetId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/performance-report`;
      
      const response = await apiClient.customPost<any>(endpoint, {});
      
      return response.data;
    } catch (error) {
      console.error(`Generate fleet performance report ${fleetId} error:`, error);
      throw error;
    }
  }

  async updateFleetConfig(fleetId: ID, config: any): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/config`;
      
      const response = await apiClient.customPut<any>(endpoint, config);
      
      this.clearFleetCache(fleetId);
      
      return response.data;
    } catch (error) {
      console.error(`Update fleet config ${fleetId} error:`, error);
      throw error;
    }
  }

  async getFleetConfig(fleetId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.FLEETS.BASE}/${fleetId}/config`;
      const cacheKey = `fleet_config_${fleetId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get fleet config ${fleetId} error:`, error);
      throw error;
    }
  }

  // ==================== PRIVATE METHODS ====================

  private generateCacheKey(prefix: string, params?: any): string {
    if (!params) return prefix;
    
    const paramString = JSON.stringify(params);
    const hash = this.hashString(paramString);
    return `${prefix}_${hash}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i);
      if (char === undefined) continue;
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private clearFleetCache(fleetId: ID): void {
    apiClient.cancelRequest(`fleet_${fleetId}`);
  }

  private clearFleetsCache(): void {
    apiClient.cancelRequest('fleets');
  }
}

// Singleton instance
export const fleetService = new FleetService();

// Default export
export default fleetService;